import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

const AuthContext = createContext();

const normalizeAuthPayload = (data) => {
  const roles = Array.isArray(data?.roles)
    ? data.roles.filter(Boolean).map((role) => String(role).toLowerCase())
    : data?.primary_role
    ? [String(data.primary_role).toLowerCase()]
    : [];

  const primaryRole = String(
    data?.primary_role || roles[0] || "user"
  ).toLowerCase();

  return {
    role: primaryRole,
    roles,
    partnerUniqueId: data?.partner_unique_id || null,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL;

  const refreshUser = async () => {
    try {
      const response = await fetch(`${apiUrl}/roles`, {
        method: "GET",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok && (data.primary_role || data.roles)) {
        const normalizedUser = normalizeAuthPayload(data);
        setUser(normalizedUser);
        setIsAuthenticated(true);
        return normalizedUser;
      }
      setUser(null);
      setIsAuthenticated(false);
      return null;
    } catch (error) {
      console.error("Ошибка при загрузке данных:", error);
      setUser(null);
      setIsAuthenticated(false);
      return null;
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        await refreshUser();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [apiUrl]);

  // 🔹 Функция логина (обновляет контекст при входе)
  const login = (userData) => {
    setUser(normalizeAuthPayload(userData));
    setIsAuthenticated(true);
  };

  // 🔹 Функция выхода (сбрасывает контекст)
  const logout = async () => {
    try {
      await fetch(`${apiUrl}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Ошибка при выходе:", error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return <div>Загрузка...</div>;
  }

  const hasRole = (rolesToCheck) => {
    const expectedRoles = Array.isArray(rolesToCheck) ? rolesToCheck : [rolesToCheck];
    const currentRoles = user?.roles || [];
    return expectedRoles.some((role) => currentRoles.includes(String(role).toLowerCase()));
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, hasRole, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired, // children должен быть элементом (или чем-то, что можно отобразить)
};

export const useAuth = () => useContext(AuthContext);
